/**
 * Greystone team — single source of truth for the AboutPage + ContactPage broker
 * cards (previously duplicated in both files). Bios are VERBATIM from Chelston /
 * the old greystonefinance.com.au site — do not paraphrase (SEO + client sign-off).
 *
 * Socials: Facebook + Instagram are the company profiles on every card; LinkedIn is
 * the company page for everyone except Brodie (his personal profile).
 * Headshots exist for Chelston/Brodie only; Brett + Clavance use an initials
 * avatar until their photos are supplied.
 */

export interface Broker {
  name: string
  title: string
  email: string
  /** Full bio; paragraphs separated by a blank line (\n\n). */
  bio: string
  /** Short specialty line for compact displays (Contact page). */
  focus: string
  /** Headshot path, or null → initials avatar. */
  image: string | null
  initials: string
  socials: { facebook?: string; instagram?: string; linkedin?: string }
  /** Calendly booking URL; undefined → "Book a call" points to /contact. */
  calendly?: string
  /**
   * false → a non-broker team member (e.g. admin/support): shown in the About
   * team grid, but hidden from the ContactPage "Speak to a broker" list and shown
   * without a booking CTA. Defaults to true (a bookable broker).
   */
  bookable?: boolean
}

export const COMPANY_SOCIALS = {
  facebook: 'https://www.facebook.com/people/Greystone-Finance/100095163582692/',
  instagram: 'https://www.instagram.com/greystone.finance/',
  linkedin: 'https://au.linkedin.com/company/greystone-finance',
}

export const BROKERS: Broker[] = [
  {
    name: 'Chelston Alvares',
    title: 'Managing Director / Principal Finance Broker',
    email: 'chelston.alvares@greystonefinance.com.au',
    focus: 'Residential lending & first home buyers',
    image: '/assets/team/chelston-alvares.png',
    initials: 'CA',
    socials: COMPANY_SOCIALS,
    calendly: 'https://calendly.com/chelston-alvares-greystonefinance',
    bio: `Chelston Alvares is the Director and Co-Founder of Greystone Finance. Recognised for his dependability and exceptional professionalism, Chelston is dedicated to guiding each client toward financial security and success.

His career began in Banking spanning over 8 years, where he oversaw a portfolio of residential clients, providing expertise in building client wealth and providing recommendations personalised to each client's needs and goals.

Chelston prides himself on providing transparent and honest advice, ensuring you have a clear understanding of the mortgage process and your options. He will guide you through the process and ensure a smooth and successful homeownership journey.`,
  },
  {
    name: 'Brodie Callanan',
    title: 'Managing Director / Principal Finance Broker',
    email: 'brodie.callanan@greystonefinance.com.au',
    focus: 'Refinancing & investment portfolios',
    image: '/assets/team/brodie-callanan.png',
    initials: 'BC',
    socials: { ...COMPANY_SOCIALS, linkedin: 'https://www.linkedin.com/in/brodie-callanan/' },
    calendly: 'https://calendly.com/brodie-callanan/30min',
    bio: `With nearly a decade of experience in financial services and banking, Brodie brings a wealth of knowledge and a genuine passion for helping clients achieve their financial goals. His mission is simple: to make securing finance straightforward, stress-free, and tailored to your unique needs. Whether you're buying your first home, expanding your investment portfolio, growing your business, or simply reviewing your current lending, Brodie is committed to saving you time and money while delivering a personalised, transparent, and results-driven experience.

At Greystone Finance, Brodie has assisted clients from a huge range of backgrounds and industries, Australia-wide. He is especially passionate about helping property investors build wealth through strategic portfolio growth. Beyond securing the right finance, Brodie connects his clients with trusted professionals (accountants, buyers' agents, real estate agents, conveyancers and financial planners), creating a holistic support network to help them thrive in today's competitive property market.

Whether you're buying your first home, expanding your investments, or reviewing your current loans, Brodie's focus is on saving you time, reducing stress, and delivering results that set you up for long-term success.`,
  },
  {
    name: 'Brett Callanan',
    title: 'Director - Commercial Lending',
    email: 'brett@greystonefinance.com.au',
    focus: 'Commercial & development finance',
    image: '/assets/team/brett-callanan.jpg',
    initials: 'BC',
    socials: COMPANY_SOCIALS,
    calendly: 'https://calendly.com/brett-greystonefinance/30min',
    bio: `Having worked in the banking industry for over 12 years Brett has experience is providing a tailored lending solution to each of his client's needs. Whether it be your next home loan, commercial property purchase or business cashflow need he can help formulate a financial solution to suit your particular scenario.

Brett has assisted clients across a range of industries including mining services, accounting and professional services, manufacturers, Construction and Property Investors/Developers. Always looking to solve a problem he will take the time to understand your goals and purpose to ensure his clients are set up for success in the future.`,
  },
  {
    name: 'Clavance Alvares',
    title: 'Associate Broker',
    email: 'clavance.alvares@greystonefinance.com.au',
    focus: 'Purchases, refinancing & loan structuring',
    image: '/assets/team/clavance-alvares.jpg',
    initials: 'CA',
    socials: COMPANY_SOCIALS,
    // No Calendly → "Book a call" points to the contact form.
    bio: `With over two years of experience in the mortgage brokering industry Clavance specialises in guiding clients toward smart, informed decisions. Whether it's purchasing a property, refinancing, or structuring a loan Clavance takes pride in simplifying complex financial processes, comparing lender options and refining applications to highlighting valuable loan features such as offset accounts and redraw facilities that can deliver meaningful long-term benefits.

Clavance's approach is thoughtful, precise, and always centred around the client. He's passionate about turning financial goals into clear, actionable plans and continually refines his communication to ensure every step of the journey feels seamless and strategically guided.`,
  },
  {
    name: 'Kristina Roycroft',
    title: 'Lead Administrator',
    email: 'kristina@greystonefinance.com.au',
    focus: 'Applications, processing & client support',
    image: '/assets/team/kristina-roycroft.jpg',
    initials: 'KR',
    socials: COMPANY_SOCIALS,
    // Non-broker team member: shown on About, hidden from the ContactPage broker list.
    bookable: false,
    bio: `Kristina Roycroft is Greystone Finance's Lead Administrator, bringing more than 10 years of mortgage broking experience to the team.

Her extensive background in broking gives Kristina a thorough understanding of the lending process and what it takes to deliver a seamless journey from application through to settlement. Having worked closely with clients and lenders throughout her career, she brings a wealth of practical knowledge and industry insight to Greystone's administrative operations.

With a practical approach and a genuine commitment to making things run smoothly, Kristina's knowledge across both broking and administration allows her to anticipate what's needed, streamline processes and contribute to the high standard of service Greystone Finance delivers to every client.`,
  },
]
